import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { TextVariant } from '@naf/text';
import { useDispatch } from 'react-redux';
import { Tabs } from '@naf-simba/sections-tabs';
import { isTabItem } from '@utils/simba/isTabItem';
import { Link } from '@naf-simba/elements-links';
import { OpenInNew } from '@naf-simba/building-blocks-icons/Arrows';
import { Button } from '@naf-simba/elements-buttons';
import { NotFound } from '@loadable-elements/NotFound';
import { useLocationDocument } from '../../useLocationDocument';
import { LoaderContent } from '../../../LoaderPage';
import RoadsideSummer from '../../../../../assets/images/RoadsideSummer.svg?url';
import { LocalDepartmentType } from '../../../../../../types/localDepartmentType';
import { actions } from '../../../../redux/modules/benefits';
import { actions as eventActions } from '../../../../redux/modules/nafEvents';
import { actions as localArticleActions } from '../../../../redux/modules/localArticles';
import useSelector from '../../../../redux/typedHooks';
import EventList from '../../components/EventList/EventList';
import { Spinner } from '../../../../components/spinner/Spinner';
import BreadCrumb from '../../../../components/breadcrumb/BreadCrumb';
import { useWindowLocation } from '../../../../hooks/Window/useWindowLocation';
import { useSendGTMEventOnce } from '../../../../hooks/GTM/useSendGTMEventOnce';
import ArticleGrid from '../../../../components/article-grid/ArticleGrid';
import { useHandleNafLocationsRoutes } from '../../useHandleNafLocationsRoutes';
import NafLocalEntryCard from '../../../../components/entry-cards/NafLocalEntryCard';
import { InternalSearchCardType } from '../../../../components/layout/InternalSearchMetaData';
import { LayoutWithMainContent } from '../../../../components/layout/Layout';
import BlockContent from '../../../../components/block-content/BlockContent';
import { useQueryParams } from '../../../../hooks/useQueryParameters';
import * as S from './Styles';
import { BlockContentContext } from '../../../../components/block-content/BlockContent.utils';

// Serves both the lokalavdelinger and mc-klubber routes.
const LocalDepartment = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useDispatch();
  const { document: departmentData, meta } = useLocationDocument<LocalDepartmentType>(slug);
  const { benefitsList, isUpdating: isUpdatingBenefits } = useSelector((state) => state.benefits);
  const { monthlyEvents, total, fetchState } = useSelector((state) => state.nafEvents);
  const {
    pages,
    total: totalLocalArticles,
    isUpdating: isUpdatingArticles,
    errorState,
  } = useSelector((state) => state.localArticles);
  const eventsReturned = monthlyEvents.reduce((acc, curr) => acc + curr.events.length, 0);
  const [take, setTake] = React.useState(6);
  const url = useWindowLocation();
  const { locations } = useHandleNafLocationsRoutes();
  const params = useQueryParams();
  const hasBenefits = Object.values(benefitsList).length > 0;
  const hasArticles = totalLocalArticles > 0;

  useEffect(() => {
    const fetchData = () => {
      dispatch(actions.getBenefits.request(`organizer=${slug}`));
      dispatch(eventActions.getNafEvents.request({ organizer: slug, take }));
      dispatch(localArticleActions.getLocalArticles.request(slug));
    };

    fetchData();
  }, [take, dispatch, slug]);

  useSendGTMEventOnce(
    departmentData && {
      page_type: 'nafLocationsApp',
      pageCategory: `Her finner du NAF / ${Object.values(locations).find((l) => url.includes(l.fullUrl))?.name} / ${
        departmentData.title
      }`,
      contentId: departmentData.id,
    },
    [departmentData],
  );

  const selectedTab = useMemo(() => {
    const localHasBenefits = Object.values(benefitsList).length > 0;
    const localHasArticles = totalLocalArticles > 0;

    if (typeof params?.tab === 'string' && ['benefits', 'events', 'articles'].includes(params.tab)) {
      return params.tab;
    }

    if (monthlyEvents.length > 0) return 'events';
    if (localHasArticles) return 'articles';
    if (localHasBenefits) return 'benefits';

    return 'benefits';
  }, [params, monthlyEvents, totalLocalArticles, benefitsList]);

  // `selectedTab` is derived, and settles as events/articles/benefits arrive. Seed the selection
  // from it and re-seed whenever it moves, so a click still wins until the derived value changes —
  // the behaviour the legacy Tabs got from its own `selectedTab` effect.
  const [activeTab, setActiveTab] = useState(selectedTab);
  useEffect(() => {
    setActiveTab(selectedTab);
  }, [selectedTab]);

  if (!departmentData && meta.isUpdating) {
    return <LoaderContent />;
  }
  if (!departmentData) {
    return <NotFound />;
  }

  const {
    title,
    ingress,
    body,
    image,
    phoneNumber,
    phoneOpeningHours,
    email,
    facebook,
    seoConfig,
    localReferenceCardList,
  } = departmentData;

  const noHitText = (
    <>
      {`Det er for tiden ikke lagt ut noen arrangementer fra ${title}.`}
      {email ? (
        <>
          {' Har du lyst til å engasjere deg lokalt? Ta gjerne kontakt med oss på '}
          <a href={`mailto:${email}`}>{email}</a> for å høre hvordan du kan engasjere deg.
        </>
      ) : (
        ' Har du lyst til å engasjere deg lokalt? Ta gjerne kontakt med oss.'
      )}
    </>
  );

  const isLoadingEventsOrBenefitsOrArticles =
    fetchState === 'initial' ||
    (fetchState === 'isUpdating' && monthlyEvents.length === 0) ||
    isUpdatingBenefits ||
    isUpdatingArticles;

  return (
    <LayoutWithMainContent
      title={title}
      description={seoConfig?.introduction || ingress}
      isHiddenFromSearch={seoConfig?.isHiddenFromSearch}
      seoDescription={seoConfig?.introduction || undefined}
      seoTitle={seoConfig?.title || undefined}
      imgUrl={image?.url || RoadsideSummer}
      internalSearchMetaData={{ cardType: InternalSearchCardType.LocalDepartment, imageUrl: image?.url }}
      url={url}
    >
      <BreadCrumb />
      <S.GridWrapper>
        <S.Header variant={TextVariant.Header1}>{title}</S.Header>
        <S.Picture src={image?.url || RoadsideSummer} alt={image?.caption || 'Bergingsbil'} />
        {ingress && <S.Ingress variant={TextVariant.Ingress}>{ingress}</S.Ingress>}
        {body && (
          <S.BodyContainer>
            <BlockContent context={BlockContentContext.Stripped} value={body} />
          </S.BodyContainer>
        )}
        {facebook ? (
          <S.Facebook>
            <Link endIcon={OpenInNew} href={facebook} text="Se siste nytt på Facebook" size="small" target="_blank" />
          </S.Facebook>
        ) : (
          <S.FacebookPlaceholder />
        )}
      </S.GridWrapper>
      <S.ContactWrapper>
        <S.StyledContactBox phone={phoneNumber} email={email} phoneOpeningHours={phoneOpeningHours} />
        {localReferenceCardList?.map((card) => (
          <S.StyledEntryCard key={card.title}>
            <NafLocalEntryCard card={card} />
          </S.StyledEntryCard>
        ))}
      </S.ContactWrapper>
      {!hasArticles && !hasBenefits && isLoadingEventsOrBenefitsOrArticles && <Spinner />}
      {(hasBenefits || hasArticles) && (
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          syncWithUrl
          items={[
            {
              value: 'events',
              label: 'Arrangementer',
              content: (
                <>
                  <EventList monthlyEvents={monthlyEvents} noHitText={noHitText} />
                  {typeof total !== 'undefined' && total > 0 && (
                    <S.ShowMoreButtonWrapper>
                      {eventsReturned < total && (
                        <Button variant="outlined" onClick={() => setTake(Math.min(take + 10, total))}>
                          {fetchState === 'isUpdating' ? <Spinner /> : 'Vis flere'}
                        </Button>
                      )}
                      <p>{`Viser ${eventsReturned} av ${total} arrangementer`}</p>
                    </S.ShowMoreButtonWrapper>
                  )}
                </>
              ),
            },
            hasBenefits && {
              value: 'benefits',
              label: 'Lokale medlemsfordeler',
              content: (
                <>
                  <S.StyledBenefitsGrid benefitItems={benefitsList} />
                  <S.YellowLine />
                  <S.StyledText variant={TextVariant.Header2}>
                    NAF har også fordeler for alle medlemmer i hele landet.
                  </S.StyledText>
                  <S.StyledLink to="/medlemskap/medlemsfordeler">
                    <Button type="button">Se alle medlemsfordeler</Button>
                  </S.StyledLink>
                </>
              ),
            },
            (hasArticles || isUpdatingArticles) && {
              value: 'articles',
              label: 'Aktuelt',
              content: Object.entries(pages).map(([key, { data: articlePageData, isLoading }]) => (
                <ArticleGrid
                  key={key}
                  articleItems={articlePageData}
                  updating={isLoading}
                  error={!!errorState}
                  showNewsLabel
                />
              )),
            },
          ].filter(isTabItem)}
        />
      )}
      {!isLoadingEventsOrBenefitsOrArticles && !(hasBenefits || hasArticles) && (
        <>
          <S.EventHeader variant={TextVariant.Header2}>Arrangement</S.EventHeader>
          <EventList monthlyEvents={monthlyEvents} noHitText={noHitText} />
          {typeof total !== 'undefined' && total > 0 && (
            <S.ShowMoreButtonWrapper>
              {eventsReturned < total && (
                <Button variant="outlined" onClick={() => setTake(Math.min(take + 10, total))}>
                  {fetchState === 'isUpdating' ? <Spinner /> : 'Vis flere'}
                </Button>
              )}
              <p>{`Viser ${eventsReturned} av ${total} arrangementer`}</p>
            </S.ShowMoreButtonWrapper>
          )}
        </>
      )}
    </LayoutWithMainContent>
  );
};

export default LocalDepartment;
